import Add from "@mui/icons-material/Add";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";
import Close from "@mui/icons-material/Close";
import { Alert, Button, Chip, Tooltip, Typography } from "@mui/material";
import { memo, useEffect, useRef } from "react";
import { formatDistance } from "../../../../utils/googleMapsUtils";
import useGoogleRoute from "../../../commons/hooks/useGoogleRoute";
import { StyledCommonForms } from "../../../styled/form";
import { StyledGoogleMaps } from "../../../styled/googleMaps";
import FormSectionDivider from "../formSectionDivider";
import {
  canAddTramo,
  canUsePersistedRouteDistances,
  copyTramoGestion,
  createEmptyPunto,
  getPuntoHeaderTitle,
  hasPersistedSegmentDistance,
  insertPuntoBeforeRetorno,
  isPuntoAddressLocked,
  isPuntoRetornoAuto,
  isTramoEditable,
  locationFromCoords,
  movePunto,
} from "./fptTrasladosUtils";
import PuntoForm from "./puntoForm";

const puntoCoords = (punto) =>
  locationFromCoords(
    punto?.location?.lat ?? punto?.latitud,
    punto?.location?.lng ?? punto?.longitud,
  );

const RecorridoList = ({
  puntos = [],
  values,
  tramosDefaults,
  disabled,
  setFieldValue,
  fields,
  idArt,
  fechaHoraTurno,
  idEstadoInicial,
  showMap = true,
  isRepeatForm = false,
}) => {
  const { getDistanceBetweenPoints } = useGoogleRoute(idArt);
  const getDistanceRef = useRef(getDistanceBetweenPoints);
  getDistanceRef.current = getDistanceBetweenPoints;
  const requestIdRef = useRef(0);
  const prevLocationKeyRef = useRef(null);
  const puntosRef = useRef(puntos);
  puntosRef.current = puntos;
  const locationKey = puntos
    .map((punto) => {
      const coords = puntoCoords(punto);
      return coords ? `${coords.lat},${coords.lng}` : "";
    })
    .join("|");

  const persistPuntos = (nextPuntos) => {
    if (nextPuntos.length < 2) return;
    puntosRef.current = nextPuntos;
    setFieldValue("puntos", nextPuntos, false);
  };

  // Distancia por campo suelto: reemplazar todo `puntos` pisa el número
  // que el usuario está tipeando con un snapshot viejo.
  const recalculateDistances = async ({ skipPersisted = false } = {}) => {
    const working = puntosRef.current;
    if (!working || working.length < 2) return;

    const requestId = ++requestIdRef.current;
    if (working[0]?.distanceMeters !== 0) {
      setFieldValue("puntos.0.distanceMeters", 0, false);
      setFieldValue("puntos.0.distanciaLabel", "0 km", false);
    }

    for (let index = 1; index < working.length; index += 1) {
      const origin = puntoCoords(puntosRef.current[index - 1]);
      const destination = puntoCoords(puntosRef.current[index]);
      if (!origin || !destination) continue;

      const current = puntosRef.current[index];
      if (skipPersisted && hasPersistedSegmentDistance(current)) {
        continue;
      }

      const { distanceMeters: routeDistanceMeters } =
        await getDistanceRef.current(origin, destination);
      if (requestId !== requestIdRef.current) return;

      const distanceMeters = routeDistanceMeters ?? null;
      const distanciaLabel = formatDistance(distanceMeters) || "";
      if (
        current?.distanceMeters === distanceMeters &&
        current?.distanciaLabel === distanciaLabel
      ) {
        continue;
      }
      setFieldValue(`puntos.${index}.distanceMeters`, distanceMeters, false);
      setFieldValue(`puntos.${index}.distanciaLabel`, distanciaLabel, false);
    }
  };

  useEffect(() => {
    const currentPuntos = puntosRef.current;
    const hasRoute = currentPuntos.some(
      (punto, index) =>
        index > 0 &&
        puntoCoords(currentPuntos[index - 1]) &&
        puntoCoords(punto),
    );
    if (!hasRoute) return undefined;

    if (prevLocationKeyRef.current === locationKey) {
      return undefined;
    }

    const previousKey = prevLocationKeyRef.current;
    prevLocationKeyRef.current = locationKey;

    if (
      previousKey === null &&
      canUsePersistedRouteDistances(currentPuntos, puntoCoords)
    ) {
      return undefined;
    }

    const skipPersisted =
      previousKey === null &&
      !canUsePersistedRouteDistances(currentPuntos, puntoCoords);

    const timeoutId = window.setTimeout(() => {
      recalculateDistances({ skipPersisted });
    }, 800);
    return () => window.clearTimeout(timeoutId);
    // Solo recalcula distancias cuando cambian las coordenadas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationKey]);

  const distanciaAcumulada = puntos.reduce(
    (total, punto) => total + (punto.distanceMeters || 0),
    0,
  );
  const addTramoBlocked = !isRepeatForm && !canAddTramo(puntos, idEstadoInicial);
  const hasLockedTramos =
    !isRepeatForm &&
    puntos
      .slice(1)
      .some((tramo) => !isTramoEditable(tramo, idEstadoInicial));

  return (
    <>
      <FormSectionDivider title="Recorrido" />

      {hasLockedTramos && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Edición parcial de traslado:</strong> Existen tramos en estado no editable (Despachados o superiores). Los puntos asociados están bloqueados: no se permite quitar ni reordenar sus puntos ni modificar sus direcciones para preservar el recorrido vigente.
        </Alert>
      )}

      {puntos.map((punto, index) => {
        const addressLocked = isPuntoRetornoAuto(punto) || (!isRepeatForm && isPuntoAddressLocked(puntos, index, idEstadoInicial));
        const isFirst = index === 0;
        const isLast = index === puntos.length - 1;
        const prevIsRetornoAuto = !isFirst && isPuntoRetornoAuto(puntos[index - 1]);
        const nextIsRetornoAuto = !isLast && isPuntoRetornoAuto(puntos[index + 1]);

        const moveUpLockedByTramo =
          !isFirst &&
          !prevIsRetornoAuto &&
          !isRepeatForm &&
          (addressLocked || isPuntoAddressLocked(puntos, index - 1, idEstadoInicial));
        const moveUpDisabled = disabled || isFirst || prevIsRetornoAuto || moveUpLockedByTramo;
        const moveUpTooltip = moveUpLockedByTramo
          ? "No se puede reordenar: pertenece a un tramo no editable (Despachado o posterior) y debe preservar su origen y destino."
          : "";

        const moveDownLockedByTramo =
          !isLast &&
          !nextIsRetornoAuto &&
          !isRepeatForm &&
          (addressLocked || isPuntoAddressLocked(puntos, index + 1, idEstadoInicial));
        const moveDownDisabled = disabled || isLast || nextIsRetornoAuto || moveDownLockedByTramo;
        const moveDownTooltip = moveDownLockedByTramo
          ? "No se puede reordenar: pertenece a un tramo no editable (Despachado o posterior) y debe preservar su origen y destino."
          : "";

        const isMinPuntos = puntos.filter((p) => !isPuntoRetornoAuto(p)).length <= 2;
        const tramoAsociado = isFirst ? puntos[1] : punto;
        const deleteLockedByTramo =
          !isRepeatForm &&
          !isMinPuntos &&
          !isTramoEditable(tramoAsociado, idEstadoInicial);
        const deleteDisabled = disabled || isMinPuntos || deleteLockedByTramo;
        const deleteTooltip = deleteLockedByTramo
          ? "No se puede quitar este punto: el tramo asociado ya no es editable (Despachado o posterior)."
          : isMinPuntos
          ? "El recorrido debe tener al menos dos puntos."
          : "";

        return (
          <StyledGoogleMaps.RecorridoRow key={punto.id || index} variant="outlined">
            <StyledGoogleMaps.RecorridoRowHeader>
              <StyledGoogleMaps.RecorridoSummary>
                <StyledGoogleMaps.RecorridoIndexBadge>
                  {index + 1}
                </StyledGoogleMaps.RecorridoIndexBadge>
                <StyledGoogleMaps.RecorridoTitle variant="subtitle1">
                  {getPuntoHeaderTitle(index, puntos.length, punto)}
                </StyledGoogleMaps.RecorridoTitle>
                {punto.esRetorno ? (
                  <Chip color="primary" label="Retorno" size="small" />
                ) : null}
                {addressLocked && !isPuntoRetornoAuto(punto) && !isRepeatForm ? (
                  <Chip
                    color="warning"
                    label="Tramo no editable"
                    size="small"
                    variant="outlined"
                  />
                ) : null}
              </StyledGoogleMaps.RecorridoSummary>
              {!isPuntoRetornoAuto(punto) ? (
                <StyledGoogleMaps.RecorridoActions>
                  <Tooltip title={moveUpTooltip} arrow>
                    <span>
                      <StyledGoogleMaps.RecorridoActionButton
                        aria-label="Subir punto"
                        disabled={moveUpDisabled}
                        onClick={() => persistPuntos(movePunto(puntos, index, index - 1))}>
                        <ArrowDropUp fontSize="small" />
                      </StyledGoogleMaps.RecorridoActionButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={moveDownTooltip} arrow>
                    <span>
                      <StyledGoogleMaps.RecorridoActionButton
                        aria-label="Bajar punto"
                        disabled={moveDownDisabled}
                        onClick={() => persistPuntos(movePunto(puntos, index, index + 1))}>
                        <ArrowDropDown fontSize="small" />
                      </StyledGoogleMaps.RecorridoActionButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={deleteTooltip} arrow>
                    <span>
                      <StyledGoogleMaps.RecorridoActionButton
                        aria-label="Quitar punto"
                        disabled={deleteDisabled}
                        onClick={() => {
                          const filtered = puntos.filter(
                            (_punto, puntoIndex) => puntoIndex !== index,
                          );
                          if (index === 0 && filtered.length > 0) {
                            filtered[0] = {
                              ...filtered[0],
                              fechaHora: null,
                              idTramo: 0,
                              nroTramoUnico: null,
                              idEstado: null,
                              distanceMeters: 0,
                              distanciaLabel: "0 km",
                            };
                          }
                          persistPuntos(filtered);
                        }}>
                        <Close fontSize="small" />
                      </StyledGoogleMaps.RecorridoActionButton>
                    </span>
                  </Tooltip>
                </StyledGoogleMaps.RecorridoActions>
              ) : null}
            </StyledGoogleMaps.RecorridoRowHeader>

            {addressLocked && !isPuntoRetornoAuto(punto) && !isRepeatForm ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", px: 2, pt: 0.5, fontStyle: "italic" }}>
                Punto bloqueado: pertenece a un tramo en estado Despachado o no editable. No se puede quitar, reordenar ni cambiar su dirección.
              </Typography>
            ) : null}

            <StyledGoogleMaps.RecorridoRowBody>
              <PuntoForm
                addressLocked={addressLocked}
                disabled={disabled}
                fechaHoraTurno={fechaHoraTurno}
                prevFechaHora={index > 0 ? puntos[index - 1]?.fechaHora : null}
                fields={fields}
                idArt={idArt}
                index={index}
                setFieldValue={setFieldValue}
                showMap={showMap}
                values={values}
              />
            </StyledGoogleMaps.RecorridoRowBody>
          </StyledGoogleMaps.RecorridoRow>
        );
      })}

      <StyledCommonForms.RowContainer>
        <Tooltip
          title={
            addTramoBlocked
              ? "No se puede agregar un tramo: el retorno ya fue autorizado."
              : ""
          }>
          <span>
            <Button
              disabled={disabled || addTramoBlocked}
              onClick={() =>
                persistPuntos(
                  insertPuntoBeforeRetorno(
                    puntos,
                    createEmptyPunto(copyTramoGestion(tramosDefaults)),
                  ),
                )
              }
              startIcon={<Add />}
              variant="outlined">
              Agregar tramo
            </Button>
          </span>
        </Tooltip>
      </StyledCommonForms.RowContainer>

      <Typography sx={{ mb: 2 }}>
        Distancia acumulada: {formatDistance(distanciaAcumulada) || "0 km"}
      </Typography>
    </>
  );
};

export default memo(RecorridoList, (prev, next) => (
  prev.disabled === next.disabled &&
  prev.showMap === next.showMap &&
  prev.idArt === next.idArt &&
  prev.fields === next.fields &&
  prev.fechaHoraTurno === next.fechaHoraTurno &&
  prev.idEstadoInicial === next.idEstadoInicial &&
  prev.puntos === next.puntos &&
  (prev.puntos?.length ?? 0) === (next.puntos?.length ?? 0) &&
  prev.tramosDefaults === next.tramosDefaults
));
